import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, Coins, Handshake, HandHelping, Search, Sparkles, UserRoundPen, Users, Cake } from "lucide-react";
import { Avatar, Button, IconButton, Menu, Pill, Spinner, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { useSession } from "@/app/session";
import { useOpenContact } from "@/app/navigation";
import { api } from "@/api/client";
import { useCredits, useMatchCards } from "@/features/network";
import { MatchModal } from "@/features/network/components/MatchModal";
import { useMatchOpener } from "@/features/network/components/useMatchOpener";
import { CREDITS_VISIBLE } from "@/features/network/lib/credits";
import { buildLocalFocus, creditBalanceLabel, fromServerItems, localDay, snoozeUntil, type FocusItem, type FocusKind } from "./focus";
import s from "../shared.module.css";

function iconFor(kind: FocusKind) {
  switch (kind) {
    case "reconnect": return <Users size={14} />;
    case "enrich": return <UserRoundPen size={14} />;
    case "intro": return <Handshake size={14} />;
    case "celebrate": return <Cake size={14} />;
  }
}

/**
 * The first card of the Today section. Server focus items when signed in; otherwise reconnect suggestions and close
 * people without needs or offers, computed locally. Always rendered: without needs or offers of your own it asks for them.
 */
export function TodaysFocusCard() {
  const navigate = useNavigate();
  const open = useOpenContact();
  const { isSignedIn } = useSession();
  const server = useQuery({ queryKey: ["focus", "today"], queryFn: () => api.focus.today(), enabled: isSignedIn, staleTime: 5 * 60_000, retry: false });
  const credits = useCredits();
  const today = localDay();
  const suggestions = useEngineQuery("getReconnectSuggestions", { localDate: today });
  const enrich = useEngineQuery("getEnrichSuggestions", { localDate: today });
  const cards = useMatchCards();
  const self = useEngineQuery("getSelfContact", undefined);
  const serverIds = (server.data?.items ?? []).map((i) => i.contactId).filter((x): x is string => !!x);
  const names = useEngineQuery("getContactRows", { contactIds: serverIds }, { enabled: serverIds.length > 0 });

  const useServer = isSignedIn && server.isSuccess;
  const items: FocusItem[] = useMemo(() => {
    const waiting = (cards.data?.cards ?? []).filter((c) => c.section === "waiting_on_you").length;
    const local = buildLocalFocus({ suggestions: suggestions.data, candidates: enrich.data?.contacts ?? [], waitingMatches: waiting });
    if (useServer) {
      const map = Object.fromEntries((names.data ?? []).map((r) => [r.contactId, r.displayName]));
      // The server knows about introductions; its reconnect and enrich rows are placeholders that this device fills
      // with real people from the local engine.
      const concrete = fromServerItems(server.data!.items, map).filter((i) => i.contactId || i.matchId || i.kind === "intro");
      const seen = new Set(concrete.map((i) => i.contactId).filter(Boolean));
      const merged = [...concrete, ...local.filter((i) => !(i.kind === "intro" && concrete.some((c) => c.kind === "intro")) && !(i.contactId && seen.has(i.contactId)))];
      if (merged.length > 0) return merged.slice(0, 4);
    }
    return local;
  }, [useServer, server.data, names.data, cards.data, suggestions.data, enrich.data]);

  const loading = (isSignedIn && server.isLoading) || suggestions.isLoading || enrich.isLoading;
  // The day's picks were made and everything on them is handled: say so instead of refilling the card.
  const finished = (suggestions.data?.picked ?? 0) + (enrich.data?.picked ?? 0) > 0;
  const selfHasItems = self.data ? self.data.needs.some((n) => !n.isCompleted) || self.data.services.some((sv) => sv.isActive) : false;
  const showPrompt = self.isSuccess && !selfHasItems;

  const opener = useMatchOpener();
  const act = (item: FocusItem) => {
    if (item.kind === "intro") {
      // The same popup the Matches list uses; the full page stays for links that arrive from outside.
      const card = (cards.data?.cards ?? []).find((c) => c.matchId === item.matchId);
      if (card) opener.open(card);
      else navigate(item.matchId ? `/matches/${item.matchId}` : "/matches");
    } else if (item.contactId) open(item.contactId, { page: item.kind === "enrich" });
  };

  return (
    <div className={s.stat} style={{ gridColumn: "1 / -1", borderColor: "var(--accent)" }} data-testid="todays-focus">
      <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 6, flexWrap: "wrap" }}>
        <Sparkles size={14} /> Today's focus
        <span className="u-grow" />
        {CREDITS_VISIBLE && isSignedIn && credits.data && (
          <button type="button" onClick={() => navigate("/network?tab=shares")} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }} title="Credits pay for introductions you accept. Open credits and shares." data-testid="focus-credits">
            <Pill tone="accent"><Coins size={11} /> {creditBalanceLabel(credits.data.status.creditsRemaining)}</Pill>
          </button>
        )}
        {isSignedIn && server.isError && <span className="u-small u-muted" title="The server could not be reached; showing what Hub knows on this device.">Offline picks</span>}
      </div>
      {showPrompt && (
        <div style={{ padding: 10, borderRadius: 8, background: "var(--accent-soft)", marginBottom: items.length ? 10 : 0 }} data-testid="focus-prompt">
          <div style={{ fontWeight: 600 }}>Tell Hub what you need and what you offer</div>
          <div className="u-small u-muted" style={{ margin: "2px 0 8px" }}>Matches start with you. One line each is enough; both stay private to your connections.</div>
          <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }}>
            <Button size="sm" variant="primary" icon={<Search size={13} />} onClick={() => navigate("/needs?new=1")}>Add what you need</Button>
            <Button size="sm" icon={<HandHelping size={13} />} onClick={() => navigate("/services?new=1")}>Add what you offer</Button>
          </div>
        </div>
      )}
      {loading && items.length === 0 && <Spinner />}
      {!loading && items.length === 0 && !showPrompt && <div className="u-small u-muted" data-testid="focus-empty">{finished ? "You are done for today. Hub keeps watching." : "Nothing needs your attention today. Hub keeps watching."}</div>}
      <div className="u-col" style={{ gap: 6 }}>
        {items.map((item) => (
          <FocusRow key={item.id} item={item} onOpen={() => act(item)} />
        ))}
      </div>
      <MatchModal card={opener.modal?.card ?? null} initialAction={opener.modal?.action ?? null} onClose={opener.close} />
    </div>
  );
}

function FocusRow({ item, onOpen }: { item: FocusItem; onOpen: () => void }) {
  const row = useEngineQuery("getContactRows", { contactIds: item.contactId ? [item.contactId] : [] }, { enabled: !!item.contactId });
  const snooze = useEngineMutation("snoozeReconnect", { invalidate: ["getReconnectSuggestions", "getTimeline"] });
  const r = row.data?.[0];
  const name = r?.displayName ?? "them";
  const later = (days: number, label: string) => {
    if (!item.contactId) return;
    snooze.mutate({ contactId: item.contactId, until: snoozeUntil(days) }, { onSuccess: () => toast(label) });
  };
  return (
    <div className="u-row" style={{ gap: 8, cursor: "pointer" }} onClick={onOpen} data-testid={`focus-item-${item.kind}`}>
      {r ? <Avatar name={r.displayName} src={r.avatarUrl} size={28} tier={r.bucket} seed={r.contactId} /> : <span className={s.feedIcon} style={{ width: 28, height: 28 }}>{iconFor(item.kind)}</span>}
      <div className="u-grow" style={{ minWidth: 0 }}>
        <div className="u-truncate" style={{ fontWeight: 500 }}>{item.title}</div>
        {item.body && <div className="u-small u-muted u-truncate">{item.body}</div>}
      </div>
      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpen(); }}>{item.kind === "intro" ? "Review" : item.kind === "enrich" ? "Add" : "Open"}</Button>
      {item.kind === "reconnect" && item.contactId && (
        <span onClick={(e) => e.stopPropagation()}>
          <Menu
            align="right"
            trigger={<IconButton label="Not now"><Clock size={14} /></IconButton>}
            items={[
              { id: "today", label: "Not today", onSelect: () => later(1, `${name} is off today's list`) },
              { id: "week", label: "Snooze a week", onSelect: () => later(7, `Hub will suggest ${name} again in a week`) },
              { id: "month", label: "Snooze a month", onSelect: () => later(30, `Hub will suggest ${name} again in a month`) },
            ]}
          />
        </span>
      )}
    </div>
  );
}
